import React from 'react';
import { Plus, Trash2, Pencil, Eye, EyeOff, GripVertical, ChevronUp, ChevronDown, Copy } from 'lucide-react';
import { Button } from '../ui/button';
import { sectionLabel, sectionRowTitle } from './sectionDefs';
import { SectionEditor } from './SectionEditor';

const SectionRow = ({ s, i, total, expanded, onToggleExpand, onMove, onDuplicate, onDelete, onToggleVisible, dragging, dragHandlers, children }) => (
  <div
    className={`bg-white border rounded-xl transition-colors ${dragging ? 'border-stone-400 opacity-60' : 'border-stone-200'}`}
    draggable={!expanded}
    {...dragHandlers}
    data-testid={`section-row-${i}`}
  >
    <div className="px-3 py-3 flex items-center gap-2 flex-wrap">
      <span className="cursor-grab text-stone-300 hover:text-stone-500 touch-none"><GripVertical className="w-4 h-4" /></span>
      <div className="flex flex-col mr-1">
        <button className="text-stone-300 hover:text-stone-600 disabled:opacity-30" disabled={i === 0} onClick={() => onMove(i, -1)} data-testid={`section-up-${i}`}><ChevronUp className="w-3.5 h-3.5" /></button>
        <button className="text-stone-300 hover:text-stone-600 disabled:opacity-30" disabled={i === total - 1} onClick={() => onMove(i, 1)} data-testid={`section-down-${i}`}><ChevronDown className="w-3.5 h-3.5" /></button>
      </div>
      <div className="flex-1 min-w-[160px]">
        <p className={`text-sm font-medium truncate ${s.visible === false ? 'text-stone-400 line-through' : 'text-stone-900'}`}>{sectionRowTitle(s)}</p>
      </div>
      <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full whitespace-nowrap">{sectionLabel(s.type)}</span>
      <Button variant="outline" size="sm" onClick={onToggleExpand} data-testid={`section-edit-${i}`}>
        <Pencil className="w-3.5 h-3.5 mr-1" /> {expanded ? 'Close' : 'Edit'}
      </Button>
      <Button variant="ghost" size="icon" className="h-8 w-8" title="Show / hide" onClick={onToggleVisible} data-testid={`section-visibility-${i}`}>
        {s.visible === false ? <EyeOff className="w-4 h-4 text-stone-400" /> : <Eye className="w-4 h-4 text-stone-600" />}
      </Button>
      <Button variant="ghost" size="icon" className="h-8 w-8" title="Duplicate section" onClick={() => onDuplicate(i)} data-testid={`section-duplicate-${i}`}>
        <Copy className="w-4 h-4 text-stone-500" />
      </Button>
      <Button variant="ghost" size="icon" className="h-8 w-8" title="Delete section" onClick={onDelete} data-testid={`section-delete-${i}`}>
        <Trash2 className="w-4 h-4 text-red-400" />
      </Button>
    </div>
    {expanded && (
      <div className="border-t border-stone-100 px-5 py-5 bg-stone-50/50 rounded-b-xl" data-testid={`section-editor-${i}`}>
        {children}
      </div>
    )}
  </div>
);

export const SectionsTab = ({
  sections, setSections, page, setPage, setDirty,
  expandedId, setExpandedId, dragIdx, setDragIdx, onAddClick, goToCalculatorTab
}) => {
  const updateSection = (id, patch) => setSections(sections.map(s => s.id === id ? { ...s, ...patch } : s));

  const duplicateSection = (idx) => {
    const copy = { ...JSON.parse(JSON.stringify(sections[idx])), id: `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}` };
    const next = [...sections];
    next.splice(idx + 1, 0, copy);
    setSections(next);
  };

  const moveSection = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= sections.length) return;
    const next = [...sections];
    [next[idx], next[j]] = [next[j], next[idx]];
    setSections(next);
  };

  const onDrop = (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) return setDragIdx(null);
    const next = [...sections];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(targetIdx, 0, moved);
    setSections(next);
    setDragIdx(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <p className="text-sm text-stone-500">Drag sections to reorder the page. Toggle the eye to show/hide. Click <b>Edit</b> to change section content.</p>
        <Button onClick={onAddClick} className="bg-stone-900 hover:bg-stone-800" data-testid="add-section-btn">
          <Plus className="w-4 h-4 mr-1" /> Add section
        </Button>
      </div>

      {sections.length === 0 ? (
        <div className="border border-dashed border-stone-300 rounded-xl py-16 text-center">
          <p className="text-stone-500 text-sm">This page has no sections yet.</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={onAddClick} data-testid="add-first-section-btn">
            <Plus className="w-4 h-4 mr-1" /> Add your first section
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {sections.map((s, i) => (
            <SectionRow
              key={s.id}
              s={s}
              i={i}
              total={sections.length}
              expanded={expandedId === s.id}
              dragging={dragIdx === i}
              onToggleExpand={() => setExpandedId(expandedId === s.id ? null : s.id)}
              onMove={moveSection}
              onDuplicate={duplicateSection}
              onToggleVisible={() => updateSection(s.id, { visible: s.visible === false })}
              onDelete={() => {
                if (window.confirm('Delete this section?')) setSections(sections.filter(x => x.id !== s.id));
              }}
              dragHandlers={{
                onDragStart: () => setDragIdx(i),
                onDragOver: (e) => e.preventDefault(),
                onDrop: () => onDrop(i),
                onDragEnd: () => setDragIdx(null)
              }}
            >
              <SectionEditor
                type={s.type}
                data={s.data || {}}
                onChange={data => updateSection(s.id, { data })}
                page={page}
                onPageChange={p => { setPage(p); setDirty(true); }}
                goToCalculatorTab={goToCalculatorTab}
              />
            </SectionRow>
          ))}
        </div>
      )}
    </div>
  );
};
